import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { FilterCriteria } from './FilterCriteria';
import { FormControl } from '@angular/forms';
import { RoleResponse } from 'projects/shared/services/roles/models/role.response';
import { RoleService } from 'projects/shared/services/roles/role.service';
import { TranslateService } from '@ngx-translate/core';
import { getErrorMessage } from 'projects/shared/utils/getErrorMessage';

@Component({
  selector: 'shared-list-users-filter',
  standalone: false,
  styles: [
    `
      .popover {
        width: 16.5rem;
        display: inline-grid;
      }

      .role-filter nz-checkbox-group ::ng-deep {
        padding-left: 16px;
        .ant-checkbox-group-item {
          width: 100%;
        }
      }

      .search-input::ng-deep > nz-select {
        width: 32rem;
      }

      nz-badge.custom-badge ::ng-deep {
        .ant-badge-count {
          background-color: #37a94c;
        }
      }

      .action-row {
        gap: 1rem;
        display: flex;
        justify-content: space-between;
      }

      .popover .ant-checkbox-wrapper {
        margin-left: 0;
      }
    `,
  ],
  template: `
    <div class="action-row">
      <mac-input-select
        class="search-input"
        [dropdownStyle]="{ display: 'none' }"
        mode="tags"
        [tokenSeparators]="[' ']"
        [allowClear]="true"
        [placeholder]="'Portal.InspectionCompanies.Users.All.Filters.Name.NoFilters.Label' | translate"
        [control]="namesFormControl"
        [options]="[]">
      </mac-input-select>
      <nz-badge class="custom-badge" [nzCount]="badgeCount">
        <mac-button
          icon="filter_alt"
          variant="tertiary"
          nz-popover
          [nzPopoverContent]="popoverContent"
          nzPopoverPlacement="bottomLeft"
          nzPopoverTrigger="click">
          {{ 'Portal.InspectionCompanies.Users.All.Filter.Label' | translate }}
        </mac-button>
      </nz-badge>
    </div>
    <ng-template #popoverContent>
      <div class="popover">
        <label nz-checkbox [(ngModel)]="hideActiveUsers" (ngModelChange)="updateHideActiveUsersChecked()">
          {{ 'Portal.InspectionCompanies.Users.Filter.HideActiveUsers' | translate }}
        </label>
        <label nz-checkbox [(ngModel)]="hidePendingUsers" (ngModelChange)="updateHidePendingUsersChecked()">
          {{ 'Portal.InspectionCompanies.Users.Filter.HidePendingUsers' | translate }}
        </label>
        <label nz-checkbox [(ngModel)]="allChecked" (ngModelChange)="updateAllChecked()" [nzIndeterminate]="indeterminate">
          {{ 'Portal.InspectionCompanies.Users.Filter.Roles' | translate }}
        </label>
        <div class="role-filter">
          <div *ngIf="!isLoadingRoles; else loadingRolesTemplate">
            <ng-container *ngIf="!loadingRolesError; else loadingRolesErrorTemplate">
              <nz-checkbox-group [formControl]="rolesFormControl"></nz-checkbox-group>
            </ng-container>
          </div>
        </div>
      </div>
    </ng-template>

    <ng-template #loadingRolesTemplate>
      <nz-spin style="width: 100px; height: 100px"></nz-spin>
    </ng-template>

    <ng-template #loadingRolesErrorTemplate>
      <app-alert type="error" [message]="'Shared.Validation.Error.CouldNotLoadRoles' | translate"> </app-alert>
    </ng-template>
  `,
})
export class ListUsersFilterComponent implements OnInit, OnDestroy {
  @Input({ required: true }) filterCriteria$: BehaviorSubject<FilterCriteria>;
  @Input({ required: true }) roles$: Observable<RoleResponse[]>;

  loadingRolesError: string;
  isLoadingRoles: boolean;
  rolesFormControl = new FormControl<CheckboxOption[] | null>([]);
  namesFormControl = new FormControl<string[] | null>([]);
  allRoles: RoleResponse[] = [];
  private namesSubscription: Subscription;
  private rolesSubscription: Subscription;
  private translateSubscription: Subscription;

  hideActiveUsers = false;
  hidePendingUsers = false;
  allChecked = false;
  indeterminate = false;
  badgeCount?: number;

  constructor(private translateService: TranslateService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.loadRoles();
    this.listenForFilterCriteriaChanges();
    this.subscribeToFormChanges();
    this.subscribeToLanguageChanges();
  }

  ngOnDestroy(): void {
    this.namesSubscription.unsubscribe();
    this.rolesSubscription.unsubscribe();
    this.filterCriteria$.unsubscribe();
    this.translateSubscription.unsubscribe();
  }

  updateAllChecked(): void {
    this.indeterminate = false;
    const roles: CheckboxOption[] = this.getRolesAsCheckboxOptions(this.allChecked);
    this.setBadgeCount(roles);
    this.rolesFormControl.setValue(roles);
  }

  private updateStatusChecked(status: string, controlFlag: boolean): void {
    const currentStatuses = this.filterCriteria$.value.statuses || [];
    const index = currentStatuses.indexOf(status);

    if (controlFlag) {
      if (index !== -1) {
        currentStatuses.splice(index, 1);
      }
    } else {
      if (index < 0) {
        currentStatuses.push(status);
      }
    }

    this.filterCriteria$.next({
      ...this.filterCriteria$.value,
      statuses: currentStatuses,
      page: 1,
    });

    this.setBadgeCount();
  }

  updateHideActiveUsersChecked(): void {
    this.updateStatusChecked('Active', this.hideActiveUsers);
  }

  updateHidePendingUsersChecked(): void {
    this.updateStatusChecked('Pending', this.hidePendingUsers);
  }

  private listenForFilterCriteriaChanges(): void {
    this.filterCriteria$.subscribe((filterCriteria) => {
      this.hidePendingUsers = !(filterCriteria.statuses || []).includes('Pending');
      this.hideActiveUsers = !(filterCriteria.statuses || []).includes('Active');
    });
  }

  private initializeForm() {
    this.namesFormControl.setValue(this.filterCriteria$.value.names || [], { emitEvent: false });

    this.setRolesToForm();
    this.setBadgeCount();
    this.updateCheckAllCheckbox();
  }

  private subscribeToLanguageChanges() {
    this.translateSubscription = this.translateService.onLangChange.subscribe(() => {
      this.cdr.detectChanges();
      this.setRolesToForm();
    });
  }

  private setRolesToForm() {
    const roles: CheckboxOption[] = this.getRolesAsCheckboxOptions();
    this.rolesFormControl.setValue(roles, { emitEvent: false });
  }

  private getRoleLabelKey(roleKey: string): string {
    return `Domain.Role.${roleKey.toLowerCase()}.Label`;
  }

  private loadRoles() {
    this.isLoadingRoles = true;
    this.roles$.subscribe({
      next: (roles) => {
        this.allRoles = roles;
        this.initializeForm();
      },
      error: (error) => {
        this.loadingRolesError = getErrorMessage(error);
        this.isLoadingRoles = false;
      },
      complete: () => {
        this.isLoadingRoles = false;
      },
    });
  }

  private subscribeToFormChanges() {
    this.namesSubscription = this.namesFormControl.valueChanges.subscribe({
      next: (value) => {
        this.filterCriteria$.next({
          ...this.filterCriteria$.value,
          names: value || [],
          page: 1,
        });
      },
    });
    this.rolesSubscription = this.rolesFormControl.valueChanges.subscribe({
      next: (values) => {
        this.filterCriteria$.next({
          ...this.filterCriteria$.value,
          roles: (values || []).filter((value) => value.checked).map((value) => value.value),
          page: 1,
        });
        this.updateCheckAllCheckbox();
        this.setBadgeCount(values);
      },
    });
  }

  private setBadgeCount(values: CheckboxOption[] | null = this.rolesFormControl.value): void {
    let badgeCount = values?.filter((x) => x.checked).length ?? 0;
    if (this.hideActiveUsers) badgeCount++;
    if (this.hidePendingUsers) badgeCount++;
    this.badgeCount = badgeCount;
  }

  private getRolesAsCheckboxOptions(isAllChecked: boolean | null = null): CheckboxOption[] {
    return this.allRoles.map((role) => ({
      label: this.translateService.instant(this.getRoleLabelKey(role.key)),
      value: role.key,
      checked: isAllChecked == null ? (this.filterCriteria$.value.roles || []).includes(role.key) : isAllChecked,
    }));
  }

  private updateCheckAllCheckbox() {
    const checkedCount = this.rolesFormControl.value?.filter((x) => x.checked).length || 0;
    this.allChecked = checkedCount === this.allRoles.length;
    this.indeterminate = checkedCount > 0 && checkedCount < this.allRoles.length;
  }
}

export interface CheckboxOption {
  label: string;
  value: string;
  checked?: boolean;
}
